import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TemplateFrame } from "@/components/common/TemplateFrame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Palette,
  Shield,
  Wand2,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  MonitorSmartphone,
  Smartphone,
  ChevronDown,
  AlertCircle,
  Loader2,
  Lock,
} from "lucide-react";
import { ImageUploadPlaceholder } from "@/components/customer/events/ImageUploadPlaceholder";
import { GalleryUploader } from "@/components/customer/events/GalleryUploader";
import { FeatureGate } from "@/components/common/FeatureGate";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { getPasswordPlaceholder } from "@/i18n/helpers";
import { AutosaveStatus } from "@/components/common/AutosaveStatus";
import { readAutosaveDraft, useAutosave } from "@/hooks/useAutosave";
import { waitForPendingSaves } from "@/hooks/autosaveRegistry";
import type { Event } from "@/services/api/events";
import {
  getEventLandingSettings,
  updateEventLandingSettings,
  uploadHeroImage,
  deleteHeroImage,
  getEventPreviewToken,
  type EventLandingSettings,
  type EventCeremony,
  type EventFamily,
  type LandingDraftOverrides,
} from "@/services/api/eventSettings";
import { useTemplateCatalog } from "@/hooks/useTemplateCatalog";
import { buildPreviewHref, encodeDraftParam } from "@/utils/previewLink";
import { cn } from "@/lib/utils";
import {
  FALLBACK_TEMPLATES,
  resolveTemplateDefinition,
  getFallbackTemplate,
  type EventTemplateId,
  EVENT_TEMPLATE_DEFAULT,
} from "@/lib/event-templates";
import { renderEventTemplate } from "@/pages/public/templates";
import { isTemplatePackage } from "@/lib/template-engine/resolve";
import { editorChromeOf, isOfferedTemplate, templateCardPreviewOf } from "@/lib/template-engine/editorTemplate";
import type { PublicEvent } from "@/services/api/publicEvents";

interface LandingTabProps {
  event: Event;
  locale: string;
}

const fontScaleOptions = [
  { id: "small", label: "Small" },
  { id: "medium", label: "Medium" },
  { id: "large", label: "Large" },
] as const;

type CeremonyKey = "civil" | "religious" | "party";

const toTemplateTranslator =
  (t: (key: string, params?: Record<string, string | number>) => string) =>
  (key: string, params?: Record<string, string | number | undefined>) =>
    t(key, params as unknown as Record<string, string | number>);

const ceremonyLabels: Record<
  CeremonyKey,
  { title: string; description: string; defaultName: string }
> = {
  civil: {
    title: "Cununia civilă",
    description: "Locul și ora pentru ceremonia civilă",
    defaultName: "Cununia civilă",
  },
  religious: {
    title: "Cununia religioasă",
    description: "Detalii despre slujba religioasă",
    defaultName: "Cununia religioasă",
  },
  party: {
    title: "Petrecerea",
    description: "Unde continuă distracția după ceremonii",
    defaultName: "Petrecerea",
  },
};

const normalizeFamily = (family?: EventFamily): EventFamily => ({
  parents: family?.parents ?? "",
  godparents: family?.godparents ?? "",
});

const normalizeCeremony = (
  ceremony: EventCeremony | undefined,
  defaults: (typeof ceremonyLabels)[CeremonyKey],
): EventCeremony => ({
  name: ceremony?.name ?? defaults.defaultName,
  enabled: ceremony?.enabled ?? false,
  venue: ceremony?.venue ?? "",
  address: ceremony?.address ?? "",
  city: ceremony?.city ?? "",
  datetime: ceremony?.datetime ?? null,
});

const normalizeSettings = (data: EventLandingSettings): EventLandingSettings => {
  // Ensure gallery is always an array, even if API returns it as a JSON string
  let gallery = data.gallery ?? [];
  if (typeof gallery === 'string') {
    try {
      gallery = JSON.parse(gallery);
    } catch (e) {
      console.error("Failed to parse gallery JSON string:", e);
      gallery = [];
    }
  }
  if (!Array.isArray(gallery)) {
    gallery = [];
  }
  
  return {
    ...data,
    gallery,
    showTimeline: data.showTimeline ?? true,
    showGallery: data.showGallery ?? true,
    showStory: data.showStory ?? true,
    colorScheme: data.colorScheme ?? null,
    heroText: data.heroText ?? null,
    family: normalizeFamily(data.family),
    ceremonies: {
      civil: normalizeCeremony(data.ceremonies?.civil, ceremonyLabels.civil),
      religious: normalizeCeremony(data.ceremonies?.religious, ceremonyLabels.religious),
      party: normalizeCeremony(data.ceremonies?.party, ceremonyLabels.party),
    },
  };
};

const gallerySlots = 3;

/**
 * JUB-152: what this tab saves as you go. Never the gallery or cover (uploads
 * persist on their own endpoints), the password (explicit "Save password")
 * or the url (explicit, plan-gated, confirmed).
 */
const LANDING_AUTOSAVE_KEYS = [
  "theme", "fontFamily", "fontScale", "colorScheme", "story", "heroText",
  "searchable", "registryPublic", "showTimeline", "showGallery", "showStory",
] as const;
type LandingAutosavePayload = Pick<EventLandingSettings, (typeof LANDING_AUTOSAVE_KEYS)[number]>;
const LANDING_FORM_ID = "landing";

const pickLandingAutosave = (s: EventLandingSettings): LandingAutosavePayload => ({
  theme: s.theme,
  fontFamily: s.fontFamily ?? null,
  fontScale: s.fontScale ?? null,
  colorScheme: s.colorScheme ?? null,
  story: s.story ?? null,
  heroText: s.heroText ?? null,
  searchable: s.searchable,
  registryPublic: s.registryPublic,
  showTimeline: s.showTimeline ?? true,
  showGallery: s.showGallery ?? true,
  showStory: s.showStory ?? true,
});

const deepEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

type CollapsibleSectionProps = {
  title: string;
  description?: string;
  icon: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  headerClassName?: string;
  contentClassName?: string;
};

function CollapsibleSection({
  title,
  description,
  icon,
  children,
  defaultOpen = false,
  headerClassName,
  contentClassName,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = () => setOpen((prev) => !prev);

  return (
    <Card className="border-2 shadow-lg overflow-hidden">
      <CardHeader
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
        className={cn(
          "border-b cursor-pointer select-none bg-card",
          headerClassName ?? "bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20",
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 shrink-0">{icon}</span>
            <div className="min-w-0">
              <CardTitle className="text-2xl">{title}</CardTitle>
              {description && <CardDescription className="mt-1">{description}</CardDescription>}
            </div>
          </div>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-muted-foreground transition-transform duration-200",
              open ? "rotate-180" : "",
            )}
          />
        </div>
      </CardHeader>
      {open && (
        <CardContent className={cn("pt-6", contentClassName)}>
          {children}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * Renders children at a fixed virtual viewport width and scales them down to
 * the available width, so templates lay out as on a real device instead of
 * being clipped inside the narrow editor column.
 */
// The template renders inside an iframe of the virtual width (JUB-205), so its
// breakpoints follow the chosen device instead of the browser window.
function ScaledPreview({ virtualWidth, title, children }: { virtualWidth: number; title: string; children: ReactNode }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [innerHeight, setInnerHeight] = useState(0);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;
    const measure = () => {
      setScale(Math.min(1, outer.clientWidth / virtualWidth));
      setInnerHeight(inner.offsetHeight);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [virtualWidth]);

  return (
    <div ref={outerRef} className="w-full overflow-x-hidden" style={{ height: innerHeight ? innerHeight * scale : undefined }}>
      <div
        ref={innerRef}
        style={{ width: virtualWidth, transform: `scale(${scale})`, transformOrigin: "top left" }}
      >
        <TemplateFrame title={title} className="block h-[800px] w-full border-0" autoHeight data-testid="landing-legacy-preview">
          {children}
        </TemplateFrame>
      </div>
    </div>
  );
}

export function LandingTab({ event, locale }: LandingTabProps) {
  const { t } = useTranslation();
  useAuth();
  const { isEnabled } = useFeatureFlags();
  const [settings, setSettings] = useState<EventLandingSettings | null>(null);
  const [initialSettings, setInitialSettings] = useState<EventLandingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);
  const [autosaveInitial, setAutosaveInitial] = useState<LandingAutosavePayload | null>(null);
  const [loadTick, setLoadTick] = useState(0);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [initialSlug, setInitialSlug] = useState("");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [savingUrl, setSavingUrl] = useState(false);
  const [showUrlConfirmDialog, setShowUrlConfirmDialog] = useState(false);
  // Mobile first: most guests open an invitation on a phone, so the preview should
  // show the owner what most of their guests will actually see.
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("mobile");
  const templateDefinitions = useTemplateCatalog();

  // Check if event has premium/standard plan (can set custom URL)
  const canSetCustomUrl = useMemo(() => {
    const planCode = event?.plan?.toLowerCase() || 'free';
    return planCode === 'premium' || planCode === 'standard';
  }, [event?.plan]);

  // JUB-64: once an event has ever gone live, its public slug is frozen —
  // guests may already hold this link (WhatsApp, printed QR codes...).
  // Enforced server-side (409 event_url_locked); this just keeps the editor
  // from offering an edit that would be rejected.
  const isUrlLocked = Boolean(settings?.urlLocked);

  // Gallery limit by plan: free=0, standard=50, premium=150
  const galleryLimit = useMemo(() => {
    const planCode = event?.plan?.toLowerCase() || 'free';
    if (planCode === 'free') return 0;
    if (planCode === 'standard') return 50;
    return 150;
  }, [event?.plan]);

  // Slug change cooldown: once per week (7 days)
  const SLUG_COOLDOWN_DAYS = 7;
  const { isSlugChangeInCooldown, nextSlugChangeAllowedAt } = useMemo(() => {
    const urlChangedAt = settings?.urlChangedAt;
    if (!urlChangedAt) {
      return { isSlugChangeInCooldown: false, nextSlugChangeAllowedAt: null as Date | null };
    }
    const next = new Date(urlChangedAt);
    next.setDate(next.getDate() + SLUG_COOLDOWN_DAYS);
    const now = new Date();
    return {
      isSlugChangeInCooldown: now < next,
      nextSlugChangeAllowedAt: next,
    };
  }, [settings?.urlChangedAt]);

  // Every definition the app can render — an event on a retired design still has
  // to resolve its own palette and fonts here.
  // `GET /templates` lists active templates only. The event's own design arrives
  // with the event (`event.template`, active or not), so an event on an installed
  // package an admin disabled later still resolves to that package here and is
  // never silently shown as another design (JUB-161).
  const offerableCatalog = templateDefinitions && templateDefinitions.length > 0 ? templateDefinitions : FALLBACK_TEMPLATES;
  const templateCatalog = useMemo(() => {
    const own = event.template;
    return own?.key && own.config && !offerableCatalog.some((template) => template.key === own.key)
      ? [...offerableCatalog, own]
      : offerableCatalog;
  }, [offerableCatalog, event.template]);

  // What the chooser offers: the current designs, plus whatever this event is
  // already on. Keeping the current one listed means an owner on a retired design
  // sees what they have and is never forced to switch just by opening this tab.
  const selectableTemplates = useMemo(() => {
    // JUB-161: an installed package returned by the API (active only) is offered
    // too; the event's own design is added below only while it is current.
    const offered = offerableCatalog.filter(isOfferedTemplate);
    const current = templateCatalog.find((template) => template.key === settings?.theme);
    return current && !offered.some((template) => template.key === current.key)
      ? [current, ...offered]
      : offered;
  }, [offerableCatalog, templateCatalog, settings?.theme]);

  const activeTemplate = useMemo(() => {
    return resolveTemplateDefinition(templateCatalog, settings?.theme);
  }, [templateCatalog, settings?.theme]);

  const galleryInputs = useMemo(() => {
    if (!settings) return Array(gallerySlots).fill("");
    const next = [...settings.gallery];
    while (next.length < gallerySlots) {
      next.push("");
    }
    return next.slice(0, gallerySlots);
  }, [settings]);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      setAutosaveInitial(null);
      try {
        // JUB-152: an autosave from an earlier visit may still be in flight.
        await waitForPendingSaves(event.uuid);
        const data = await getEventLandingSettings(event.uuid);
        if (!isMounted) return;
        const normalized = normalizeSettings(data);
        // Unsaved edits from an earlier visit are laid over the server state.
        const draft = readAutosaveDraft(event.uuid, LANDING_FORM_ID) as Partial<LandingAutosavePayload> | undefined;
        setSettings(draft ? { ...normalized, ...draft } : normalized);
        setInitialSettings(normalized);
        setAutosaveInitial(pickLandingAutosave(normalized));
        setSlugInput(normalized.slug || "");
        setInitialSlug(normalized.slug || "");
      } catch (error) {
        console.error(error);
        toast.error(t("common.error"));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, [event.uuid, t, loadTick]);

  // ------------------------------------------------------------ autosave (JUB-152)
  const autosave = useAutosave<LandingAutosavePayload, EventLandingSettings>({
    current: settings ? pickLandingAutosave(settings) : ({} as LandingAutosavePayload),
    initial: autosaveInitial,
    resetKey: `${event.uuid}:${loadTick}`,
    pendingKey: event.uuid,
    formId: LANDING_FORM_ID,
    allowedKeys: LANDING_AUTOSAVE_KEYS,
    atomicKeys: ["heroText"],
    save: (patch) => updateEventLandingSettings(event.uuid, patch as Parameters<typeof updateEventLandingSettings>[1]),
    // The response is not written back into the form (it would overwrite
    // what the user typed meanwhile); only the preview-draft baseline moves.
    onSaved: (_updated, patch) => setInitialSettings((prev) => (prev ? { ...prev, ...patch } : prev)),
  });
  const { flushOnNextChange } = autosave;
  /** Switches, selects and the template chooser save right away. */
  const setSettingsNow = useCallback((next: EventLandingSettings) => {
    flushOnNextChange();
    setSettings(next);
  }, [flushOnNextChange]);
  const discardLandingDraft = () => {
    autosave.discardDraft();
    setLoadTick((n) => n + 1);
  };
  /** Media endpoints already persisted this gallery: keep form and baseline in step. */
  const applyPersistedGallery = (gallery: string[]) => {
    setSettings((prev) => (prev ? { ...prev, gallery } : prev));
    setInitialSettings((prev) => (prev ? { ...prev, gallery } : prev));
  };

  const handleTemplateSelect = (templateId: EventLandingSettings["theme"]) => {
    if (!settings) return;
    setSettingsNow({ ...settings, theme: templateId });
  };

  // Validate slug input
  const validateSlug = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return null; // Empty is allowed (nullable)
    
    // Check for path traversal prevention - only alphanumeric, hyphen, underscore allowed
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
      return t("events.form.slugInvalidChars");
    }
    
    if (trimmed.length < 7) {
      return t("events.form.slugMinLength");
    }
    
    if (trimmed.length > 255) {
      return t("events.form.slugMaxLength");
    }
    
    return null;
  };

  const handleSlugChange = (value: string) => {
    setSlugInput(value);
    setSlugError(null); // Clear error on change
    const error = validateSlug(value);
    if (error) {
      setSlugError(error);
    }
  };

  // Password protection stays an explicit action (JUB-152): enabling needs a
  // password, disabling deletes it, and a password is never sent while typing.
  const passwordDirty = Boolean(
    settings && initialSettings &&
      (settings.passwordEnabled !== initialSettings.passwordEnabled || (settings.passwordEnabled && passwordInput)),
  );
  const handleSavePassword = async () => {
    if (!settings) return;
    setSavingPassword(true);
    try {
      const payload = {
        passwordEnabled: settings.passwordEnabled,
        ...(settings.passwordEnabled && passwordInput ? { password: passwordInput } : {}),
      };
      const updated = await updateEventLandingSettings(event.uuid, payload);
      const normalized = normalizeSettings(updated);
      const passwordState = {
        passwordEnabled: normalized.passwordEnabled,
        passwordSet: normalized.passwordSet,
      };
      setSettings((prev) => (prev ? { ...prev, ...passwordState } : prev));
      setInitialSettings((prev) => (prev ? { ...prev, ...passwordState } : prev));
      setPasswordInput("");
      toast.success(t("events.form.landingSaved"));
    } catch (error: unknown) {
      console.error(error);
      const errorData = (error as { response?: { data?: { error?: { message?: string; message_key?: string } } } })?.response?.data;
      toast.error(errorData?.error?.message || t("common.error"));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveUrl = async () => {
    if (!settings) return;
    if (isUrlLocked) {
      // Defense in depth: the input/button are hidden once locked, but block
      // the call too in case this is reached via a stale render.
      setShowUrlConfirmDialog(false);
      toast.error(t("events.form.urlLockedDescription"));
      return;
    }
    const trimmedSlug = slugInput.trim();
    const validationError = validateSlug(trimmedSlug);
    if (validationError) {
      setSlugError(validationError);
      toast.error(validationError);
      return;
    }
    if (!trimmedSlug || trimmedSlug === initialSlug) {
      setShowUrlConfirmDialog(false);
      return;
    }
    setSavingUrl(true);
    setSlugError(null);
    try {
      const updated = await updateEventLandingSettings(event.uuid, { url: trimmedSlug });
      const normalized = normalizeSettings(updated);
      // Only the slug fields: autosaved fields may have edits in flight.
      const slugState = { slug: normalized.slug, urlChangedAt: normalized.urlChangedAt, urlLocked: normalized.urlLocked };
      setSettings((prev) => (prev ? { ...prev, ...slugState } : prev));
      setInitialSettings((prev) => (prev ? { ...prev, ...slugState } : prev));
      setSlugInput(normalized.slug || "");
      setInitialSlug(normalized.slug || "");
      setSlugError(null);
      setShowUrlConfirmDialog(false);
      toast.success(t("events.form.urlSaved"));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { code?: string; message?: string; message_key?: string; next_change_allowed_at?: string } }; status?: number } };
      const errorData = err.response?.data?.error;
      const status = err.response?.status;
      if (status === 409 && errorData?.code === "event_url_locked") {
        // JUB-64: server-side freeze rejected the change (should already be
        // pre-empted client-side by isUrlLocked, but the event may have gone
        // live in another tab/session since this page loaded).
        const msg = errorData.message || t("events.form.urlLockedDescription");
        setSlugError(msg);
        toast.error(msg);
        try {
          const refreshed = await getEventLandingSettings(event.uuid);
          const normalized = normalizeSettings(refreshed);
          const slugState = { slug: normalized.slug, urlChangedAt: normalized.urlChangedAt, urlLocked: normalized.urlLocked };
          setSettings((prev) => (prev ? { ...prev, ...slugState } : prev));
          setInitialSettings((prev) => (prev ? { ...prev, ...slugState } : prev));
          setSlugInput(normalized.slug || "");
          setInitialSlug(normalized.slug || "");
        } catch {
          // best-effort refresh; the error toast already informed the user
        }
      } else if (status === 429 && errorData?.code === "slug_change_cooldown") {
        const msg = errorData.message || t("events.form.urlChangeCooldown");
        setSlugError(msg);
        toast.error(msg);
      } else if (errorData?.message_key === "validation.custom_url.premium_required" || errorData?.code === "forbidden") {
        setSlugError(errorData.message || t("events.form.premiumRequired", {}));
        toast.error(errorData.message || t("events.form.premiumRequired", {}));
      } else {
        const msg = errorData?.message || (err as Error).message || t("common.error");
        setSlugError(msg);
        toast.error(msg);
      }
    } finally {
      setSavingUrl(false);
    }
  };

  const heroImageUrl = galleryInputs[0] ?? "";
  // Get frontend URL from API URL (remove /api suffix) or use window.location.origin
  // Avoid using localhost:3000 (Grafana port) - prefer environment variable or current origin if valid
  const getFrontendOrigin = () => {
    if (typeof window === "undefined") return "https://dev.wedistar.com";
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) {
      try {
        const url = new URL(apiUrl);
        // Remove /api suffix if present
        const pathname = url.pathname.replace(/\/api\/?$/, "");
        return `${url.protocol}//${url.host}${pathname}`;
      } catch {
        // Invalid URL, fall through
      }
    }
    // Only use window.location.origin if it's not localhost:3000 (Grafana)
    const origin = window.location.origin;
    if (origin.includes(":3000")) {
      // Default to dev.wedistar.com for local dev, or extract from API URL
      return apiUrl ? new URL(apiUrl).origin.replace(/\/api\/?$/, "") : "https://dev.wedistar.com";
    }
    return origin;
  };
  const baseOrigin = getFrontendOrigin();
  const sanitizedOrigin = baseOrigin.replace(/^https?:\/\//, "");
  const prettyOrigin = sanitizedOrigin;
  const pendingOverrides = useMemo<LandingDraftOverrides | null>(() => {
    if (!initialSettings || !settings) {
      return null;
    }
    const diff: LandingDraftOverrides = {};
    if (initialSettings.theme !== settings.theme) diff.theme = settings.theme;
    if ((initialSettings.fontFamily ?? "") !== (settings.fontFamily ?? "")) diff.fontFamily = settings.fontFamily;
    if ((initialSettings.fontScale ?? "medium") !== (settings.fontScale ?? "medium")) {
      diff.fontScale = settings.fontScale;
    }
    if ((initialSettings.story ?? "") !== (settings.story ?? "")) diff.story = settings.story;
    if (JSON.stringify(initialSettings.heroText ?? null) !== JSON.stringify(settings.heroText ?? null)) {
      diff.heroText = settings.heroText;
    }
    if (initialSettings.passwordEnabled !== settings.passwordEnabled) {
      diff.passwordEnabled = settings.passwordEnabled;
    }
    if (initialSettings.searchable !== settings.searchable) {
      diff.searchable = settings.searchable;
    }
    if (initialSettings.registryPublic !== settings.registryPublic) {
      diff.registryPublic = settings.registryPublic;
    }
    if (!deepEqual(initialSettings.gallery, settings.gallery)) {
      diff.gallery = settings.gallery;
    }
    if ((initialSettings.colorScheme ?? null) !== (settings.colorScheme ?? null)) {
      diff.colorScheme = settings.colorScheme ?? undefined;
    }
    // Slug/URL is saved separately via "Save URL" button - do not include in pending overrides
    return Object.keys(diff).length > 0 ? diff : null;
  }, [initialSettings, settings]);

  // Unsaved changes travel in the preview link as `draft`; they are applied
  // only when the API confirmed the owner's preview (JUB-151).
  const draftParam = useMemo(
    () => (pendingOverrides ? encodeDraftParam({ eventUuid: event.uuid, overrides: pendingOverrides }) : null),
    [event.uuid, pendingOverrides],
  );

  const [debouncedDraftParam, setDebouncedDraftParam] = useState<string | null>(draftParam);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDraftParam(draftParam), 800);
    return () => clearTimeout(timer);
  }, [draftParam]);

  // The server-signed preview token (JUB-151) is what lets the owner open an
  // unpublished or password-protected page (JUB-125). Minted ahead of the
  // click — window.open after an await would be popup-blocked — and renewed
  // before it expires while the editor stays open.
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const mint = async () => {
      try {
        const { token, expiresIn } = await getEventPreviewToken(event.uuid);
        if (cancelled) return;
        setPreviewToken(token);
        timer = setTimeout(mint, Math.max(60, expiresIn * 0.8) * 1000);
      } catch (error) {
        if (!cancelled) {
          console.warn("[LandingTab] Could not mint a preview token", error);
          setPreviewToken(null);
        }
      }
    };
    void mint();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [event.uuid]);

  // Convert Event + settings to PublicEvent format for preview
  // MUST be before early return to maintain hooks order
  const previewEvent = useMemo((): PublicEvent | null => {
    if (!settings) return null;
    
    const templateKey = (settings.theme as EventTemplateId) ?? EVENT_TEMPLATE_DEFAULT;
    const templateDefinition =
      resolveTemplateDefinition(templateCatalog, templateKey) || getFallbackTemplate(templateKey);
    
    // Apply color scheme and typography overrides from form (live preview uses current settings)
    let config = { ...templateDefinition.config };
    // A template package carries its own tokens and the engine applies the colour
    // scheme itself (from `colorScheme` below); the legacy patches would only
    // write stray fields onto it, and read `typography` it does not have (JUB-161).
    const isPackage = isTemplatePackage(templateDefinition.config);
    if (settings.colorScheme && !isPackage) {
      config = {
        ...config,
        palette: {
          ...config.palette,
          accent: settings.colorScheme,
          accentSoft: `${settings.colorScheme}33`,
          line: `${settings.colorScheme}33`,
        },
        hero: {
          ...config.hero,
          buttonBg: settings.colorScheme,
        },
        surfaces: {
          ...config.surfaces,
          chipBackground: `${settings.colorScheme}20`,
          cardBorder: `${settings.colorScheme}40`,
        },
      };
    }
    if (settings.fontFamily?.trim() && !isPackage) {
      const font = settings.fontFamily.trim();
      config = {
        ...config,
        typography: {
          ...config.typography,
          heading: `'${font}', ${config.typography.body}`,
          body: config.typography.body,
        },
      };
    }
    
    const ceremonies: PublicEvent["ceremonies"] = {
      civil: settings.ceremonies.civil.enabled
        ? {
            enabled: true,
            name: settings.ceremonies.civil.name || "",
            datetime: settings.ceremonies.civil.datetime || "",
            venue: settings.ceremonies.civil.venue || "",
            city: settings.ceremonies.civil.city || "",
            address: settings.ceremonies.civil.address || "",
          }
        : { enabled: false, name: "" },
      religious: settings.ceremonies.religious.enabled
        ? {
            enabled: true,
            name: settings.ceremonies.religious.name || "",
            datetime: settings.ceremonies.religious.datetime || "",
            venue: settings.ceremonies.religious.venue || "",
            city: settings.ceremonies.religious.city || "",
            address: settings.ceremonies.religious.address || "",
          }
        : { enabled: false, name: "" },
      party: settings.ceremonies.party.enabled
        ? {
            enabled: true,
            name: settings.ceremonies.party.name || "",
            datetime: settings.ceremonies.party.datetime || "",
            venue: settings.ceremonies.party.venue || "",
            city: settings.ceremonies.party.city || "",
            address: settings.ceremonies.party.address || "",
          }
        : { enabled: false, name: "" },
    };
    
    return {
      uuid: event.uuid,
      name: event.name,
      date: event.date,
      type: event.type,
      currency: event.currency || "RON",
      country: event.country || "",
      url: event.url || "",
      cover: heroImageUrl || undefined,
      gallery: settings.gallery || [],
      story: settings.story || event.description || "",
      heroText: settings.heroText || null,
      description: event.description || "",
      starBride: event.starBride || undefined,
      starGroom: event.starGroom || undefined,
      // JUB-95: the preview renders through the same renderEventTemplate() path
      // as the public page, but without these the type-aware branch in every
      // template (Hero, RSVP, Family) always falls back to wedding copy —
      // the preview must carry the event's real type, not just the couple names.
      starBaptism: event.starBaptism || undefined,
      starBaptism2: event.starBaptism2 || undefined,
      includesWedding: event.includesWedding ?? true,
      includesBaptism: event.includesBaptism ?? false,
      babyCount: event.babyCount ?? null,
      ceremonies,
      family: settings.family ? {
        parents: settings.family.parents || undefined,
        godparents: settings.family.godparents || undefined,
      } : undefined,
      giftsActive: event.giftsActive || false,
      registryPublic: settings.registryPublic || false,
      theme: templateKey,
      colorScheme: settings.colorScheme || undefined,
      template: { ...templateDefinition, config },
      showStory: settings.showStory ?? true,
      showTimeline: settings.showTimeline ?? true,
      showGallery: settings.showGallery ?? true,
    };
  }, [event, settings, templateCatalog, heroImageUrl]);

  const previewSlugForRequest = settings?.slug || event.url || event.uuid;
  // Use simplified route format: /@{slug}
  // The share URL stays clean; only the owner's preview link carries the
  // server-signed preview token and the unsaved draft (JUB-125, JUB-151).
  const shareUrl =
    previewSlugForRequest && baseOrigin
      ? `${baseOrigin.replace(/\/$/, "")}/@${previewSlugForRequest}`
      : "";
  const previewHref = buildPreviewHref(baseOrigin, event.uuid, previewToken, draftParam);
  /** A design change the server has not accepted yet (pending, or its save failed). */
  const themeAwaitingSave = Boolean(pendingOverrides?.theme);
  // Same origin as the editor, whatever VITE_API_URL says, so the frame is allowed
  // (X-Frame-Options SAMEORIGIN); the draft is debounced so typing does not reload it.
  const packagePreviewSrc = buildPreviewHref(
    typeof window === "undefined" ? "" : window.location.origin,
    event.uuid,
    previewToken,
    debouncedDraftParam,
  );
  const shareDisplayUrl = shareUrl ? shareUrl.replace(/^https?:\/\//, "") : "";
  const hasPendingOverrides = Boolean(pendingOverrides);
  if (loading || !settings) {
    return (
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b">
          <CardTitle className="text-2xl">{t("events.form.landingPage")}</CardTitle>
          <CardDescription className="mt-1">
            {t("events.form.landingPageDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="py-12 text-center text-muted-foreground">
          {t("common.loading")}
        </CardContent>
      </Card>
    );
  }

  const workingSlugDisplay = slugInput.trim() || settings.slug || "";

  // JUB-161: through an accessor, since an installed package has none of these
  // legacy fields.
  const {
    hero: templateHero,
    surfaces: templateSurfaces,
    typography: templateTypography,
  } = editorChromeOf(activeTemplate);

  const previewCardStyle: CSSProperties = {
    background: templateSurfaces.cardBackground,
    borderColor: templateSurfaces.cardBorder,
  };

  const handleCopyPreviewLink = () => {
    if (!shareUrl) {
      toast.error(t("events.form.slugMissing"));
      return;
    }
    // Guests get the clean URL — the draft token is owner-only (JUB-125).
    navigator.clipboard.writeText(shareUrl);
    toast.success(t("events.form.slugCopied"));
  };

  const handleOpenPreview = () => {
    if (!previewHref) {
      toast.error(t("events.form.slugMissing"));
      return;
    }
    window.open(previewHref, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border bg-card/70 p-4 shadow-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-pink-600">
            {t("events.form.landingPage")}
          </p>
          <h2 className="text-xl font-semibold text-foreground">
            {t("events.form.landingHeroHeading", {
            })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("events.form.landingHeroSubheading", {
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {hasPendingOverrides && (
            <span className="rounded-full border border-pink-200 bg-pink-50 px-3 py-1 text-xs font-semibold text-pink-600">
              {t("events.form.draftBadge")}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={handleOpenPreview}
            disabled={!previewHref}
            data-preview-url={previewHref || undefined}
          >
            <Eye className="mr-2 h-4 w-4" />
            {t("common.preview")}
          </Button>
          <AutosaveStatus
            status={autosave.status}
            restored={autosave.restored}
            onRetry={() => void autosave.retry()}
            onDiscard={discardLandingDraft}
            onSaveNow={() => void autosave.flush()}
          />
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-2 lg:items-start gap-6">
        <div className="space-y-4 lg:min-w-0">
          <CollapsibleSection
            title={t("events.form.designSettings")}
            description={t(
              isEnabled("design_color_scheme")
                ? "events.form.designSettingsDescription"
                : "events.form.designSettingsDescriptionBasic",
            )}
            icon={<Palette className="h-6 w-6 text-pink-600 dark:text-pink-400" />}
          >
            <div className="grid gap-4 md:grid-cols-3">
              {selectableTemplates.map((template) => {
                const isActive = settings?.theme
                  ? settings.theme === template.key
                  : template.key === templateCatalog[0]?.key;
                const cardPreview = templateCardPreviewOf(template);
                return (
                  <button
                    key={template.key}
                    type="button"
                    data-testid={`landing-template-card-${template.key}`}
                    data-active={isActive ? "true" : "false"}
                    onClick={() => handleTemplateSelect(template.key)}
                    className={cn(
                      "rounded-xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500",
                      isActive
                        ? "border-pink-500 shadow-lg"
                        : "border-border hover:border-pink-400",
                    )}
                    style={{
                      background: isActive ? "rgba(255,255,255,0.9)" : "var(--card)",
                    }}
                  >
                    <div
                      className="rounded-lg p-4 text-3xl"
                      style={{ background: cardPreview.background }}
                    >
                      {cardPreview.emoji}
                    </div>
                    <h4 className="mt-4 text-lg font-semibold text-foreground">
                      {t(`events.form.templateCatalog.${template.key}.name`, {
                        defaultValue: template.name,
                      })}
                    </h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t(`events.form.templateCatalog.${template.key}.tagline`, {
                        defaultValue: cardPreview.tagline,
                      })}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 space-y-4 border-t pt-6">
              {/* Parked (JUB-3): only shown when the design_color_scheme flag is ON.
                  Stored values keep applying to preview and public page. */}
              <FeatureGate flag="design_color_scheme">
                <div>
                  <p className="mb-3 text-sm font-medium text-muted-foreground">
                    {t("events.form.colorScheme")}
                  </p>
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      className="h-12 w-24 cursor-pointer"
                      value={settings.colorScheme || "#ec4899"}
                      onChange={(event) => setSettings({ ...settings, colorScheme: event.target.value })}
                      onBlur={() => void autosave.flush()}
                    />
                    <Input
                      type="text"
                      className="flex-1 font-mono text-sm"
                      placeholder="#ec4899"
                      value={settings.colorScheme || ""}
                      onChange={(event) => {
                        const value = event.target.value.trim();
                        if (value === "" || /^#[0-9A-Fa-f]{6}$/.test(value)) {
                          setSettings({ ...settings, colorScheme: value || null });
                        }
                      }}
                    />
                    {settings.colorScheme && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSettingsNow({ ...settings, colorScheme: null })}
                      >
                        {t("events.form.reset")}
                      </Button>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t("events.form.colorSchemeHint", {
                    })}
                  </p>
                </div>
              </FeatureGate>

              <div>
                <p className="mb-3 text-sm font-medium text-muted-foreground">
                  {t("events.form.sectionVisibility")}
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("events.form.showStory")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("events.form.showStoryHint")}
                      </p>
                    </div>
                    <Switch
                      checked={settings.showStory ?? true}
                      onCheckedChange={(value) => setSettingsNow({ ...settings, showStory: value })}
                    />
                  </div>
                  <div className={`flex items-center justify-between rounded-lg border p-3 ${galleryLimit === 0 ? "opacity-60" : ""}`}>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("events.form.showGallery")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {galleryLimit === 0
                          ? (t("events.form.galleryLocked") ?? "Gallery is available with Standard or Premium plans.")
                          : t("events.form.showGalleryHint")}
                      </p>
                    </div>
                    <Switch
                      checked={settings.showGallery ?? true}
                      onCheckedChange={(value) => setSettingsNow({ ...settings, showGallery: value })}
                      disabled={galleryLimit === 0}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("events.form.showTimeline")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("events.form.showTimelineHint")}
                      </p>
                    </div>
                    <Switch
                      checked={settings.showTimeline ?? true}
                      onCheckedChange={(value) => setSettingsNow({ ...settings, showTimeline: value })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t("events.form.privacy")}
            description={t("events.form.privacyDescription", {
            })}
            icon={<Shield className="h-6 w-6 text-pink-600 dark:text-pink-400" />}
            contentClassName="space-y-6"
          >
              <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {t("events.form.searchable")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("events.form.searchableHint", {
                    })}
                  </p>
                </div>
                <Switch
                  checked={settings.searchable}
                  onCheckedChange={(value) => setSettingsNow({ ...settings, searchable: value })}
                />
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {t("events.form.registryVisibility")}
                  </p>
                  <p className="text-sm text-muted-foreground" data-testid="registry-visibility-hint">
                    {settings.registryPublic
                      ? t("events.form.registryVisibilityHintEnabled")
                      : t("events.form.registryVisibilityHintDisabled")}
                  </p>
                </div>
                <Switch
                  data-testid="registry-visibility-switch"
                  aria-label={t("events.form.registryVisibility")}
                  checked={settings.registryPublic}
                  onCheckedChange={(value) => setSettingsNow({ ...settings, registryPublic: value })}
                />
              </div>

              <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {t("events.form.passwordProtect")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("events.form.passwordProtectHint", {
                      })}
                    </p>
                  </div>
                  <Switch
                    data-testid="password-protection-toggle"
                    checked={settings.passwordEnabled}
                    onCheckedChange={(value) => {
                      setSettings({ ...settings, passwordEnabled: value });
                      // Keep any typed password so toggling off/on before saving
                      // doesn't silently drop it (saving disabled clears it server-side)
                    }}
                  />
                </div>

                {settings.passwordEnabled && (
                  <div className="grid gap-2 pt-2 border-t">
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("events.form.passwordLabel")}
                    </p>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={passwordInput}
                        onChange={(event) => setPasswordInput(event.target.value)}
                        placeholder={getPasswordPlaceholder(t, {
                          passwordSet: settings.passwordSet ?? false,
                          passwordInput: passwordInput,
                        })}
                        className="pr-10"
                        maxLength={72}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {settings.passwordSet && !passwordInput && (
                      <p className="text-xs text-muted-foreground">
                        {t("events.form.passwordSetHint")}
                      </p>
                    )}
                  </div>
                )}
                {passwordDirty && (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSavePassword}
                      disabled={savingPassword || (settings.passwordEnabled && !passwordInput && !settings.passwordSet)}
                      data-testid="save-password-button"
                    >
                      {savingPassword ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("common.saving")}
                        </>
                      ) : (
                        t("autosave.savePassword")
                      )}
                    </Button>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {t("events.form.previewLinkLabel")}
                </p>
                <div className="rounded-xl border bg-background/90 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1 text-sm font-mono text-muted-foreground break-words" title={shareDisplayUrl || undefined}>
                      {shareDisplayUrl ||
                        t("events.form.slugDescription", {
                        })}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyPreviewLink}
                        disabled={!previewHref}
                      >
                        <Copy className="mr-1.5 h-4 w-4" />
                        {t("common.copy")}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleOpenPreview}
                        disabled={!previewHref}
                        data-preview-url={previewHref || undefined}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {hasPendingOverrides && (
                    <p className="mt-2 text-xs font-semibold text-pink-600">
                      {t("events.form.previewDraftNotice", {
                      })}
                    </p>
                  )}
                  <div className="mt-3 flex flex-col gap-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      {t("events.form.slugLabel")}
                    </Label>
                    {!canSetCustomUrl && (
                      <div data-testid="upgrade-guidance-banner" className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20 p-3 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-amber-900 dark:text-amber-100">
                            {t("events.form.customUrlPremiumOnly", {
                            })}
                          </p>
                          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                            {t("events.form.upgradeToUnlock", {
                            })}
                          </p>
                          <a
                            href={`/${locale}/pachete?event=${event.uuid}`}
                            className="inline-flex items-center mt-2 text-xs font-semibold text-amber-900 dark:text-amber-100 underline underline-offset-2 hover:no-underline"
                          >
                            {t("common.upgradePlan") ?? "Upgrade Plan"} →
                          </a>
                        </div>
                      </div>
                    )}
                    {isUrlLocked && (
                      <div data-testid="url-locked-banner" className="rounded-lg border border-muted-foreground/30 bg-muted/40 p-3 flex items-start gap-2">
                        <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-foreground">
                            {t("events.form.urlLockedTitle")}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("events.form.urlLockedDescription")}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {prettyOrigin.replace(/\/$/, "")}/@
                      </span>
                      <Input
                        value={slugInput}
                        onChange={(event) => handleSlugChange(event.target.value)}
                        placeholder="anamihai12312025"
                        disabled={!canSetCustomUrl || isUrlLocked}
                        data-testid="custom-url-input"
                        className={cn(slugError && "border-destructive")}
                        minLength={7}
                        maxLength={255}
                      />
                    </div>
                    {slugError && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {slugError}
                      </p>
                    )}
                    {!slugError && workingSlugDisplay && (
                      <p className="text-xs text-muted-foreground">
                        {t("events.form.slugPreviewHint", {
                          slug: workingSlugDisplay,
                        })}
                      </p>
                    )}
                    {canSetCustomUrl && !isUrlLocked && !slugError && (
                      <p className="text-xs text-muted-foreground">
                        {t("events.form.slugRequirements", {
                        })}
                      </p>
                    )}
                    {canSetCustomUrl && !isUrlLocked && isSlugChangeInCooldown && nextSlugChangeAllowedAt && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {t("events.form.urlChangeCooldownUntil", {
                          date: nextSlugChangeAllowedAt.toLocaleDateString(locale === "ro" ? "ro-RO" : "en-GB", { year: "numeric", month: "short", day: "numeric" }),
                        })}
                      </p>
                    )}
                    {canSetCustomUrl && !isUrlLocked && slugInput.trim() !== initialSlug && !isSlugChangeInCooldown && !slugError && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="mt-2"
                        onClick={() => setShowUrlConfirmDialog(true)}
                        data-testid="save-url-button"
                      >
                        {t("events.form.saveUrl")}
                      </Button>
                    )}
                  </div>
                </div>
                </div>
              </div>
          </CollapsibleSection>

          <Dialog open={showUrlConfirmDialog} onOpenChange={(open) => { setShowUrlConfirmDialog(open); if (!open) setSlugInput(initialSlug); }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("events.form.urlConfirmTitle")}</DialogTitle>
                <DialogDescription>
                  {t("events.form.urlConfirmDescription")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="cancel-url-button"
                  onClick={() => { setShowUrlConfirmDialog(false); setSlugInput(initialSlug); }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveUrl}
                  disabled={savingUrl}
                  data-testid="confirm-save-url-button"
                >
                  {savingUrl ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("common.saving")}
                    </>
                  ) : (
                    t("events.form.urlConfirmUpdate")
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <CollapsibleSection
            title={t("events.form.contentCustomization")}
            description={t("events.form.contentCustomizationDescription", {
            })}
            icon={<Wand2 className="h-6 w-6 text-pink-600 dark:text-pink-400" />}
            contentClassName="space-y-6"
          >
              {/* Story field (legacy) */}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="text-sm font-semibold">
                    {t("events.form.story")}
                  </Label>
                </div>
                <Textarea
                  rows={4}
                  value={settings.story ?? ""}
                  onChange={(event) => setSettings({ ...settings, story: event.target.value })}
                  onBlur={() => void autosave.flush()}
                  data-testid="landing-story-input"
                  placeholder={t("events.form.storyPlaceholder", {
                  })}
                />
              </div>

              {/* Hero Text (per-locale) */}
              <div className="space-y-3 border-t pt-4">
                <Label className="text-sm font-semibold">
                  {t("events.form.heroText")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("events.form.heroTextHint", {
                  })}
                </p>
                <Tabs defaultValue="ro" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="ro">Română</TabsTrigger>
                    <TabsTrigger value="en">English</TabsTrigger>
                  </TabsList>
                  <TabsContent value="ro" className="mt-3">
                    <Textarea
                      rows={4}
                      data-testid="hero-text-ro-input"
                      placeholder={t("events.form.heroTextPlaceholderRo")}
                      value={settings.heroText?.ro ?? ""}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          heroText: {
                            ...(settings.heroText || {}),
                            ro: event.target.value || undefined,
                          },
                        })
                      }
                    />
                  </TabsContent>
                  <TabsContent value="en" className="mt-3">
                    <Textarea
                      rows={4}
                      data-testid="hero-text-en-input"
                      placeholder={t("events.form.heroTextPlaceholderEn")}
                      value={settings.heroText?.en ?? ""}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          heroText: {
                            ...(settings.heroText || {}),
                            en: event.target.value || undefined,
                          },
                        })
                      }
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <ImageUploadPlaceholder
                    label={t("events.form.heroImage")}
                    currentImageUrl={heroImageUrl || null}
                    onUpload={async (file) => {
                      const result = await uploadHeroImage(event.uuid, file);
                      // The upload endpoint persisted the gallery; never re-sent by autosave.
                      applyPersistedGallery(result.gallery);
                      return result.gallery[0] ?? result.url;
                    }}
                    onChange={async (url) => {
                      if (url === null && heroImageUrl) {
                        // Null means "remove" — the hero delete endpoint persists it.
                        try {
                          await deleteHeroImage(event.uuid);
                        } catch {
                          toast.error(t("common.error"));
                          return;
                        }
                        applyPersistedGallery((settings.gallery ?? []).slice(1));
                      }
                    }}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("events.form.heroHint", {
                    })}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-semibold">
                    {t("events.form.fontScale.label")}
                  </Label>
                  <Select
                    value={settings.fontScale ?? "medium"}
                    onValueChange={(value) =>
                      setSettingsNow({
                        ...settings,
                        fontScale: value as EventLandingSettings["fontScale"],
                      })
                    }
                  >
                    <SelectTrigger className="mt-2 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fontScaleOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {t(`events.form.fontScale.${option.id}`) || option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {/* Parked (JUB-11): only shown when the design_font_family flag is ON.
                    Stored values keep applying to preview and public page. */}
                <FeatureGate flag="design_font_family">
                  <div>
                    <Label className="text-sm font-semibold">
                      {t("events.form.fontFamily")}
                    </Label>
                    <Input
                      className="mt-2"
                      placeholder={t("events.form.fontFamilyPlaceholder")}
                      value={settings.fontFamily ?? ""}
                      onChange={(event) => setSettings({ ...settings, fontFamily: event.target.value })}
                    />
                  </div>
                </FeatureGate>
                <div>
                  <Label className="text-sm font-semibold">
                    {t("events.form.gallery")}
                  </Label>
                  {galleryLimit > 0 ? (
                    <GalleryUploader
                      eventUuid={event.uuid}
                      gallery={settings.gallery ?? []}
                      limit={galleryLimit}
                      onUpdate={applyPersistedGallery}
                      onError={(msg) => toast.error(msg)}
                    />
                  ) : (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-4 text-sm text-amber-800 dark:text-amber-200">
                      {t("events.form.galleryLocked") ?? "Gallery is available with Standard or Premium plans."}
                    </div>
                  )}
                </div>
              </div>
          </CollapsibleSection>
        </div>

        <div className="space-y-4 lg:sticky lg:top-28 lg:min-w-0">
          <Card className="border-2 shadow-2xl" style={previewCardStyle}>
            <CardHeader style={{ background: templateHero.background, color: templateHero.textColor }}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-2xl" style={{ fontFamily: templateTypography.heading }}>
                    {t("events.form.preview")}
                  </CardTitle>
                  <CardDescription className="mt-1 opacity-80" style={{ color: templateHero.textColor }}>
                    {t("events.form.previewDescription", {
                    })}
                  </CardDescription>
                </div>
                {/* The header is painted with the template hero colours, so the inactive button and
                    the draft badge inherit its text colour instead of assuming a dark header (JUB-190). */}
                <div
                  className="inline-flex items-center rounded-full border p-1 text-xs font-semibold gap-1"
                  style={{ borderColor: "color-mix(in srgb, currentColor 30%, transparent)" }}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "flex items-center gap-1 rounded-full px-3 py-1 text-xs",
                      previewDevice === "desktop" ? "text-foreground" : "text-inherit opacity-75 hover:opacity-100 hover:bg-black/5 hover:text-inherit",
                    )}
                    onClick={() => setPreviewDevice("desktop")}
                    style={
                      previewDevice === "desktop"
                        ? {
                            background: templateHero.buttonBg,
                            color: templateHero.buttonText,
                            borderColor: templateHero.buttonBorder,
                          }
                        : undefined
                    }
                  >
                    <MonitorSmartphone className="h-4 w-4" />
                    {t("events.form.desktopView")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "flex items-center gap-1 rounded-full px-3 py-1 text-xs",
                      previewDevice === "mobile" ? "text-foreground" : "text-inherit opacity-75 hover:opacity-100 hover:bg-black/5 hover:text-inherit",
                    )}
                    onClick={() => setPreviewDevice("mobile")}
                    style={
                      previewDevice === "mobile"
                        ? {
                            background: templateHero.buttonBg,
                            color: templateHero.buttonText,
                            borderColor: templateHero.buttonBorder,
                          }
                        : undefined
                    }
                  >
                    <Smartphone className="h-4 w-4" />
                    {t("events.form.mobileView")}
                  </Button>
                  {hasPendingOverrides && (
                    <span
                      className="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-inherit"
                      style={{ background: "color-mix(in srgb, currentColor 12%, transparent)" }}
                    >
                      {t("events.form.draftBadge")}
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div
                className={cn(
                  "min-h-[800px] w-full overflow-x-hidden bg-[#faf9f7] relative transition-all duration-300",
                  previewDevice === "mobile" ? "mx-auto max-w-[390px]" : "mx-auto",
                )}
              >
                {previewEvent && previewEvent.template && isTemplatePackage(previewEvent.template.config) ? (
                  // JUB-161: an installed package is previewed by the real public page
                  // through the owner's signed preview link (unpublished and protected
                  // pages included, unsaved changes as `draft`), so it renders with the
                  // event's real data and access rules instead of a partial copy.
                  // Until the server has accepted a design change, that page would still
                  // show the saved design, so the frame waits instead of showing another
                  // design as if it were the selection (Retry/Discard settle it).
                  themeAwaitingSave ? (
                    <div
                      className="flex h-[800px] items-center justify-center text-muted-foreground"
                      data-testid="landing-package-preview-waiting"
                      role="status"
                    >
                      {t("events.form.previewSavingDesign")}
                    </div>
                  ) : packagePreviewSrc ? (
                    <iframe
                      key={packagePreviewSrc}
                      src={packagePreviewSrc}
                      title={t("events.form.preview")}
                      data-testid="landing-package-preview"
                      className="block h-[800px] w-full border-0"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      {t("common.loading")}
                    </div>
                  )
                ) : previewEvent && previewEvent.template && previewEvent.template.config ? (
                  <ScaledPreview virtualWidth={previewDevice === "mobile" ? 390 : 1200} title={t("events.form.preview")}>
                    {renderEventTemplate({
                      event: previewEvent,
                      config: previewEvent.template.config,
                      locale,
                      t: toTemplateTranslator(t),
                      onShare: async () => {
                        // Preview doesn't need actual share functionality
                      },
                    })}
                  </ScaledPreview>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    {t("common.loading")}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}

